import { Modal, Form, Select, Input, message } from 'antd';
import AxiosOICStat from "AxiosOICStat";
import { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

const { Option } = Select;

const ImfIndicatorMappingModal = ({ visible, onCancel, onSuccess, record }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [imfItems, setImfItems] = useState([]);
  const [imfSearch, setImfSearch] = useState('');
  const [datasets, setDatasets] = useState([]);
  const searchTimer = useRef(null);
  const indicatorsState = useSelector((state) => state.indicators);

  const availableIndicators = indicatorsState.value
    ? Object.values(indicatorsState.value).sort((a, b) => (a.ind_name_eng || '').localeCompare(b.ind_name_eng || ''))
    : [];

  useEffect(() => {
    if (visible) {
      fetchMappings();
      fetchDatasets();
      fetchImfItems('');
      setImfSearch('');
    }
  }, [visible]);

  const fetchMappings = async () => {
    try {
      const res = await AxiosOICStat.get('/external_api/imf/indicator-mappings');
      setMappings(res.data?.mappings || res.data || []);
    } catch (e) {
      message.error(e?.response?.data?.detail || e?.message || 'Failed to load IMF indicator mappings');
    }
  };

  const fetchDatasets = async () => {
    try {
      const res = await AxiosOICStat.get('/external_api/imf/datasets');
      setDatasets(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      message.error(e?.response?.data?.detail || e?.message || 'Failed to load IMF datasets');
    }
  };

  const fetchImfItems = async (search = '') => {
    try {
      const q = [];
      if (typeof search === 'string' && search.trim()) q.push(`search=${encodeURIComponent(search.trim())}`);
      q.push('limit=200');
      const url = `/external_api/imf/get-all-imf-data?${q.join('&')}`;
      const res = await AxiosOICStat.get(url);
      const items = res.data || res.data?.items || [];
      setImfItems(Array.isArray(items) ? items : []);
    } catch (e) {
      message.error(e?.response?.data?.detail || e?.message || 'Failed to load IMF indicators');
    }
  };

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchImfItems(imfSearch || '');
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [imfSearch]);

  useEffect(() => {
    if (visible && record) {
      form.setFieldsValue({
        oic_ind_code: record.oic_ind_code ? String(record.oic_ind_code) : undefined,
        oic_ind_name: record.oic_ind_name,
        imf_indicator_code: record.imf_indicator_code,
        dataset: record.dataset,
        imf_indicator_label: record.imf_indicator_label,
      });
      if (record.imf_indicator_code) {
        const exists = imfItems.find(i => String(i.imf_indicator_code) === String(record.imf_indicator_code) && String(i.dataset) === String(record.dataset));
        if (!exists) {
          setImfItems(prev => [{
            imf_indicator_code: record.imf_indicator_code,
            dataset: record.dataset,
            imf_indicator_label: record.imf_indicator_label,
          }, ...prev]);
        }
      }
    }
    if (!visible && !record) form.resetFields();
  }, [visible, record, form, imfItems]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      if (record && record.id) {
        await AxiosOICStat.put(`/external_api/imf/indicator-mappings/${record.id}`, values);
        message.success('Indicator mapping updated');
      } else {
        await AxiosOICStat.post('/external_api/imf/indicator-mappings', values);
        message.success('Indicator mapping created');
      }
      onSuccess();
    } catch (err) {
      if (err.errorFields) return;
      const detail = err?.response?.data?.detail || err?.message || 'Failed to save mapping';
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const validateUnique = (field, val) => {
    if (!val) return true;
    return !mappings.some(m => String(m[field] || '') === String(val) && (!record || String(m.id) !== String(record.id)));
  };

  // Build unique IMF indicator options from items
  const imfOptionsMap = new Map();
  imfItems.forEach(item => {
    const key = `${item.imf_indicator_code}___${item.dataset || ''}`;
    if (!imfOptionsMap.has(key)) {
      imfOptionsMap.set(key, {
        imf_indicator_code: item.imf_indicator_code,
        dataset: item.dataset,
        imf_indicator_label: item.imf_indicator_label,
      });
    }
  });
  const imfOptions = Array.from(imfOptionsMap.values());

  return (
    <Modal
      title={record ? 'Edit IMF Indicator Mapping' : 'Add IMF Indicator Mapping'}
      visible={visible}
      onOk={handleSubmit}
      onCancel={() => { form.resetFields(); onCancel(); }}
      confirmLoading={loading}
      width={600}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="oic_ind_code"
          label="OIC Indicator"
          rules={[{ required: true, message: 'Please select an OIC indicator' }]}
        >
          <Select
            showSearch
            autoClearSearchValue={false}
            allowClear
            placeholder="Select an indicator"
            onChange={(val) => {
              const sel = availableIndicators.find(i => String(i.ind_code) === String(val));
              if (sel) {
                form.setFieldsValue({ oic_ind_name: sel.ind_name_eng });
              }
            }}
            optionFilterProp="children"
          >
            {availableIndicators.map(ind => (
              <Option key={String(ind.ind_code)} value={String(ind.ind_code)}>{ind.ind_code} - {ind.ind_name_eng}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="oic_ind_name" style={{ display: 'none' }}>
          <Input />
        </Form.Item>

        <Form.Item
          name="imf_indicator_code"
          label="IMF Indicator"
          rules={[
            { required: true, message: 'Please select an IMF indicator' },
            { validator: (_, val) => {
              if (!val) return Promise.resolve();
              if (!validateUnique('imf_indicator_code', val)) return Promise.reject(new Error('IMF indicator code already mapped'));
              return Promise.resolve();
            }}
          ]}
        >
          <Select
            showSearch
            autoClearSearchValue={false}
            allowClear
            placeholder="Select an IMF indicator"
            optionFilterProp="children"
            filterOption={false}
            onSearch={(val) => setImfSearch(val)}
            onChange={(val) => {
              const sel = imfOptions.find(i => String(i.imf_indicator_code) === String(val));
              if (sel) {
                form.setFieldsValue({
                  imf_indicator_code: sel.imf_indicator_code,
                  dataset: sel.dataset || null,
                  imf_indicator_label: sel.imf_indicator_label || null,
                });
              }
            }}
          >
            {imfOptions.map(it => (
              <Option key={it.imf_indicator_code + it.dataset} value={it.imf_indicator_code}>
                {it.imf_indicator_code} {it.dataset ? `(${it.dataset})` : ''} - {it.imf_indicator_label || ''}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="dataset" label="Dataset">
          <Select allowClear showSearch optionFilterProp="children" placeholder="Select dataset">
            {datasets.map(ds => (
              <Option key={ds.code} value={ds.code}>{ds.code} - {ds.name}</Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="imf_indicator_label" label="IMF Indicator Label">
          <Input.TextArea rows={2} placeholder="Auto-filled when selecting above" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ImfIndicatorMappingModal;
